/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * NOTE: This file contains components that are used in the Canvas block rendering.
 * !! If you're adding an entry add it to /blocks/Entries - This is for you future self. !!
 */

import { ComponentBlock, RenderBlockProps } from '@contensis/canvas-react'
import ReactMarkdown from 'react-markdown'

import IntroText from '../Text/IntroText/IntroText'
import StandardText from '../Text/StandardText/StandardText'
import Alert from '../UI/General/Alert/Alert'
import FeatureItem from '~/components/Content/FeatureItem/FeatureItem'
import GeneralAccordion from '~/components/Content/Accordion/GeneralAccordion'
import ThirdsLayout from '~/layouts/Core/layoutThirds'
import HalvesLayout from '~/layouts/Core/layoutHalves'
import WholeLayout from '~/layouts/Core/layoutWhole'
import CTABlock from '../UI/Buttons/helpers/CTABlock'
import BlockQuote from '../Content/BlockQuote/BlockQuote'
import EntryContent from './blocks/Entries'
import { useLayoutItems } from '.'
import DownloadButton from '../UI/Buttons/DownloadButton/DownloadButton'
import TertiaryLink from '../UI/Buttons/TertiaryLink/TertiaryLink'

type ImgContemtComponent = { content: string; leftRight: boolean; image: any }

export function EntryBlock(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  return (
    <EntryContent block={data.entry} backgroundColour={data.backgroundColour} />
  )
}

export function ThirdsLayoutBlock(
  props: RenderBlockProps<ComponentBlock<any>>
) {
  const data = props.block?.value
  return (
    <ThirdsLayout
      content={{ ...data, edgetoedgeBackground: true }}
      padding={'none'}
    />
  )
}

export function HalvesLayoutBlock(
  props: RenderBlockProps<ComponentBlock<any>>
) {
  const data = props.block?.value
  return (
    <HalvesLayout
      content={{ ...data, edgetoedgeBackground: true }}
      padding={'none'}
      className='[&_div>h2]:text-blue-m [&_div>h2]:font-serif [&_div>h2]:text-[100px]/[115%] [&_div>h2]:font-normal'
    />
  )
}

export function WholeLayoutBlock(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  return (
    <WholeLayout
      content={{ ...data, edgetoedgeBackground: true }}
      padding={'none'}
    />
  )
}

export function CTAs(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  const layoutItems = useLayoutItems()

  return (
    <CTABlock
      content={data?.buttons}
      ctaButtons={data?.ctaButtons}
      backgroundColour={layoutItems?.layoutColour as AllBackgroundColours}
    />
  )
}

export function CTADownload(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  const layoutItems = useLayoutItems()

  const colour = ['ink-blue', 'mid-blue'].includes(layoutItems?.layoutColour)
    ? 'white'
    : 'ink-blue'

  return data?.tertiary ? (
    <TertiaryLink
      colour={'mid-blue'}
      data={{ ...data, internalLink: data?.internalFile }}
    />
  ) : (
    <DownloadButton data={data} colour={colour} className='w-fit' />
  )
}

export function Eyebrow(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  return <p className='tag mb-xs'>{data.eyebrow}</p>
}
export function IntroTextBlock(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  const layoutItems = useLayoutItems()

  return <IntroText content={data} layoutColour={layoutItems?.layoutColour} />
}

export function StandardTextBlock(
  props: RenderBlockProps<ComponentBlock<any>>
) {
  const data = props.block?.value
  return <StandardText content={data} />
}

export function AlertBlock(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  return <Alert content={data} />
}

export function QuoteBlock(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  const layoutItems = useLayoutItems()

  return <BlockQuote content={data} layoutColour={layoutItems?.layoutColour} />
}

export function FeatureItemBlock(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  return <FeatureItem content={data} />
}

export function AccordionBlock(props: RenderBlockProps<ComponentBlock<any>>) {
  const data = props.block?.value
  return <GeneralAccordion content={data} className='mx-0 [&>div]:mx-0' />
}

export function ImageContent(
  props: RenderBlockProps<ComponentBlock<ImgContemtComponent>>
) {
  const data = props.block?.value
  return (
    <section className='pt-6'>
      <div
        className={`relative flex flex-col gap-[50px] sm:gap-20 md:flex-row ${data?.leftRight ? 'md:flex-row-reverse' : 'md:flex-row'}`}
      >
        <div className='basis-1/2'>
          <ReactMarkdown className='space-y-5'>{data?.content}</ReactMarkdown>
        </div>
        <div className='sticky top-2 h-full basis-1/2' role='presentation'>
          <picture>
            <source
              srcSet={
                (process.env.NODE_ENV === 'development'
                  ? 'https://preview-uni-hull-dev.cloud.contensis.com'
                  : '') +
                data?.image?.asset?.sys.uri +
                (data?.image?.asset?.sys.uri.includes('?') ? '&' : '?') +
                'f=webp'
              }
              type='image/webp'
            />
            <img
              className='max-w-auto mx-auto h-[275px] sm:h-[400px] md:h-auto md:max-w-[100%]'
              loading='lazy'
              src={
                (process.env.NODE_ENV === 'development'
                  ? 'https://preview-uni-hull-dev.cloud.contensis.com'
                  : '') + data?.image?.asset?.sys.uri
              }
              alt={data?.image?.altText}
            />
          </picture>
          <div className='bg-blue-d-10 px-2 py-1'>{data?.image.caption}</div>
        </div>
      </div>
    </section>
  )
}
